/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetRiskOverviewRequest,
  GetRiskOverviewSecurity,
} from "../models/operations/getriskoverview.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskOverviewQuery,
  prefetchRiskOverview,
  queryKeyRiskOverview,
  RiskOverviewQueryData,
} from "./riskOverview.core.js";
export {
  buildRiskOverviewQuery,
  prefetchRiskOverview,
  queryKeyRiskOverview,
  type RiskOverviewQueryData,
};

export type RiskOverviewQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getRiskOverview risk
 *
 * @remarks
 * Get risk overview metrics and trend data for the current project.
 */
export function useRiskOverview(
  request?: GetRiskOverviewRequest | undefined,
  security?: GetRiskOverviewSecurity | undefined,
  options?: QueryHookOptions<RiskOverviewQueryData, RiskOverviewQueryError>,
): UseQueryResult<RiskOverviewQueryData, RiskOverviewQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskOverviewQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getRiskOverview risk
 *
 * @remarks
 * Get risk overview metrics and trend data for the current project.
 */
export function useRiskOverviewSuspense(
  request?: GetRiskOverviewRequest | undefined,
  security?: GetRiskOverviewSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskOverviewQueryData,
    RiskOverviewQueryError
  >,
): UseSuspenseQueryResult<RiskOverviewQueryData, RiskOverviewQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskOverviewQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskOverviewData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      from?: Date | undefined;
      to?: Date | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskOverviewQueryData,
): RiskOverviewQueryData | undefined {
  const key = queryKeyRiskOverview(...queryKeyBase);

  return client.setQueryData<RiskOverviewQueryData>(key, data);
}

export function invalidateRiskOverview(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      from?: Date | undefined;
      to?: Date | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "overview", "get", ...queryKeyBase],
  });
}

export function invalidateAllRiskOverview(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "overview", "get"],
  });
}
